import type { Dayjs } from 'dayjs'
import type { ExtendedDayjs } from '@tunasong/models'
import { dayjs } from '@tunasong/models'
import type { CalendarEvent } from '@tunasong/schemas'
import { DatePicker, TimePicker, Divider, UserList, FormControlLabel, Checkbox, TextField } from '@tunasong/ui-lib'
import type { FC } from 'react'
import React from 'react'

import type { HTMLAttributes } from 'react'
import styles from './event-edit.module.css'

export interface CalendarEventEditProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  event: CalendarEvent
  // Include the title in the editor. @default true
  includeTitle?: boolean
  onPartialChange: (event: Partial<CalendarEvent>) => void
}

export const CalendarEventEdit: FC<CalendarEventEditProps> = props => {
  const { event, includeTitle = true, onPartialChange, ...divProps } = props

  const userIds = event.attendees?.map(a => a.userId) ?? []

  const currentStart = dayjs(event.start) as ExtendedDayjs
  const currentEnd = dayjs(event.end) as ExtendedDayjs

  const handleName = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPartialChange({ name: e.target.value })
  }

  const handleLocation = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPartialChange({ location: e.target.value })
  }

  const handleDate = (e: Dayjs | null) => {
    const start = e ? e : dayjs()
    const end = start.add(1, 'hour')
    onPartialChange({ start: start.toISOString(), end: end.toISOString() })
  }
  /** To / from dates. We want to keep the interval */
  const handleFromChange = (e: Dayjs | null) => {
    if (!e) {
      return
    }
    const start = currentStart.set('hour', e.hour()).set('minute', e.minute())
    onPartialChange({ start: start.toISOString(), end: currentEnd.toISOString() })
  }
  const handleToChange = (e: Dayjs | null) => {
    if (!e) {
      return
    }
    const end = currentStart.set('hour', e.hour()).set('minute', e.minute())
    onPartialChange({ start: currentStart.toISOString(), end: end.toISOString() })
  }
  const handleAllDay = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.checked) {
      onPartialChange({ allDay: true })
    } else {
      onPartialChange({ allDay: false })
    }
  }
  const handleRemoveUser = (userId: string) => {
    onPartialChange({ attendees: event.attendees?.filter(a => a.userId !== userId) ?? [] })
  }

  return (
    <div {...divProps} key={event.id}>
      {includeTitle ? (
        <TextField
          defaultValue={event.name}
          fullWidth
          title="Event name"
          variant="standard"
          label="Event name"
          onChange={handleName}
        />
      ) : null}

      <TextField
        defaultValue={event.location}
        fullWidth
        title="Location"
        variant="standard"
        label="Location"
        onChange={handleLocation}
        className={styles.locationField}
      />

      <DatePicker
        className={styles.datePicker}
        label="Date and time of event"
        defaultValue={dayjs(event?.start)}
        onChange={handleDate}
      />

      <FormControlLabel
        className={styles.allDayControl}
        control={<Checkbox defaultChecked={false} onChange={handleAllDay} />}
        label="All-day event"
      />

      {!event.allDay ? (
        <div className={styles.timeRow}>
          <TimePicker disabled={event.allDay} label="From" value={currentStart} onChange={handleFromChange} />
          <TimePicker label="To" value={currentEnd} onChange={handleToChange} />
        </div>
      ) : null}
      <Divider className={styles.divider}>Participants</Divider>
      <UserList userIds={userIds} onRemove={handleRemoveUser} />
    </div>
  )
}

export default CalendarEventEdit
